import React, { useEffect, useState, useRef, useCallback } from 'react'
import Header from '../Header/Header'
import CustomHeading from '../../components/common/CustomHeading'
import RoughPropertyService from '../Service/RoughPropertyService'
import SittingService from '../Service/SittingService'
import { useLocation } from 'react-router-dom'
import { Timeline } from "primereact/timeline"
import "primereact/resources/themes/saga-blue/theme.css"
import "primereact/resources/primereact.min.css"
import "primeicons/primeicons.css"

const RoughPropertyHistory = () => {
  const location = useLocation();
  const { propertyId, propertyName } = location.state || {};

  // State management
  const [propertyHistory, setPropertyHistory] = useState([])
  const [sittingHistory, setSittingHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMoreProperty, setHasMoreProperty] = useState(true)
  const [hasMoreSitting, setHasMoreSitting] = useState(true)
  const [propertyPagination, setPropertyPagination] = useState({ startAfter: null })
  const [sittingPagination, setSittingPagination] = useState({ startAfter: null })
  
  // Refs for scroll containers
  const propertyScrollRef = useRef(null)
  const sittingScrollRef = useRef(null)
  const isFetchingProperty = useRef(false)
  const isFetchingSitting = useRef(false)

  // Tab management
  const tabs = ["Property History"]
  const [activeTab, setActiveTab] = useState(tabs[0])

  // Fetch Property History with pagination
  const fetchPropertyHistory = useCallback(async (isInitial = false) => {
    if (isFetchingProperty.current || (!hasMoreProperty && !isInitial)) return;
    
    isFetchingProperty.current = true;
    setLoading(isInitial);

    try {
      const params = {
        limit: 50,
        ...(propertyPagination.startAfter && { startAfter: propertyPagination.startAfter })
      };

      const response = await RoughPropertyService.getRoughPropertyHistory(propertyId, params);
      const data = response.data;

      if (data.success) {
        const formattedHistory = data.data.map(item => ({
          id: item.id,
          log: item.log,
          timestamp: `${item.dateTime}`,
          action: item.action,
          changeBy: item.changeBy,
          editDetails: item.editDetails
        }));

        if (isInitial) {
          setPropertyHistory(formattedHistory);
        } else {
          setPropertyHistory(prev => [...prev, ...formattedHistory]);
        }

        setHasMoreProperty(data.pagination?.hasMore || false);
        setPropertyPagination({ startAfter: data.pagination?.next || null });
      }
    } catch (error) {
      console.error("Error fetching property history:", error);
    } finally {
      isFetchingProperty.current = false;
      setLoading(false);
    }
  }, [propertyId, propertyPagination.startAfter, hasMoreProperty]);

  // Fetch Sitting History with pagination
  const fetchSittingHistory = useCallback(async (isInitial = false) => {
    if (isFetchingSitting.current || (!hasMoreSitting && !isInitial)) return;
    
    isFetchingSitting.current = true;
    setLoading(isInitial);

    try {
      const params = {
        limit: 50,
        ...(sittingPagination.startAfter && { startAfter: sittingPagination.startAfter })
      };

      const response = await SittingService.getSittingHistory(propertyId, params);
      const data = response.data;

      if (data.success) {
        const formattedHistory = data.data.history.map(item => ({
          id: item.id,
          sittingId: item.sittingId,
          log: item.log,
          timestamp: item.dateTime,
          action: item.action,
          changeByUser: item.changeByUser,
          editDetails: item.editDetails
        }));

        if (isInitial) {
          setSittingHistory(formattedHistory);
        } else {
          setSittingHistory(prev => [...prev, ...formattedHistory]);
        }

        setHasMoreSitting(data.data.pagination?.hasMore || false);
        setSittingPagination({ startAfter: data.data.pagination?.lastDocId || null });
      }
    } catch (error) {
      console.error("Error fetching sitting history:", error);
    } finally {
      isFetchingSitting.current = false;
      setLoading(false);
    }
  }, [propertyId, sittingPagination.startAfter, hasMoreSitting]);

  // Scroll handlers
  const handlePropertyScroll = useCallback(() => {
    const container = propertyScrollRef.current;
    if (!container || isFetchingProperty.current || !hasMoreProperty) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      fetchPropertyHistory(false);
    }
  }, [fetchPropertyHistory, hasMoreProperty]);

  const handleSittingScroll = useCallback(() => {
    const container = sittingScrollRef.current;
    if (!container || isFetchingSitting.current || !hasMoreSitting) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      fetchSittingHistory(false);
    }
  }, [fetchSittingHistory, hasMoreSitting]);

  // Initial data fetch
  useEffect(() => {
    if (propertyId) {
      fetchPropertyHistory(true);
      fetchSittingHistory(true);
    }
  }, [propertyId]);

  // Attach scroll listeners
  useEffect(() => {
    const propertyContainer = propertyScrollRef.current;
    const sittingContainer = sittingScrollRef.current;

    if (propertyContainer && activeTab === "Property History") {
      propertyContainer.addEventListener('scroll', handlePropertyScroll);
      return () => propertyContainer.removeEventListener('scroll', handlePropertyScroll);
    }

    if (sittingContainer && activeTab === "Sitting History") {
      sittingContainer.addEventListener('scroll', handleSittingScroll);
      return () => sittingContainer.removeEventListener('scroll', handleSittingScroll);
    }
  }, [handlePropertyScroll, handleSittingScroll, activeTab]);

  // Utility function to format action names
  const formatActionName = (action) => {
    if (!action) return '';
    return action
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Empty state component
  const EmptyHistoryState = ({ title, description }) => (
    <div className="text-center py-20 flex flex-col items-center justify-center">
      <svg viewBox="0 0 240 180" className="w-48 h-36 mx-auto mb-6">
        <defs>
          <linearGradient id="historyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#1E40AF" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="clockGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366F1" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#4F46E5" stopOpacity="0.6" />
          </linearGradient>
        </defs>
        
        <rect x="80" y="50" width="80" height="100" rx="8" fill="url(#historyGrad)" stroke="#E5E7EB" strokeWidth="2" />
        <line x1="95" y1="70" x2="145" y2="70" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" />
        <line x1="95" y1="80" x2="135" y2="80" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" />
        <line x1="95" y1="90" x2="140" y2="90" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" />
        
        <circle cx="120" cy="110" r="20" fill="url(#clockGrad)" stroke="#4F46E5" strokeWidth="2" />
        <circle cx="120" cy="110" r="15" fill="none" stroke="#FFFFFF" strokeWidth="1.5" />
        <line x1="120" y1="110" x2="120" y2="100" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="120" y1="110" x2="127" y2="110" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="120" cy="110" r="2" fill="#FFFFFF" />
        
        <circle cx="50" cy="70" r="3" fill="#93C5FD" opacity="0.6" />
        <circle cx="190" cy="85" r="2.5" fill="#60A5FA" opacity="0.5" />
        <circle cx="200" cy="60" r="3.5" fill="#3B82F6" opacity="0.4" />
      </svg>
      
      <div className="text-slate-600 text-xl font-medium mb-2">{title}</div>
      <div className="text-slate-400 text-sm max-w-xs">{description}</div>
    </div>
  );

  return (
    <div>
      <Header />
      <div className='px-6 pt-28'>
        <CustomHeading title={propertyName ? `${propertyName} - History` : "Property History"} isIcon={true} />

        <div className='mt-10 bg-white p-6 rounded-lg'>
          {/* Tabs */}
          {/* <div className="flex space-x-1 w-1/2 bg-gray-100 p-1 rounded-lg mb-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                  activeTab === tab
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab}
              </button>
            ))}
          </div> */}

          {/* Content Area */}
          <div className="min-h-96">
            {activeTab === "Property History" && (
              <div>
                <div className="bg-slate-100 p-3 mb-4 rounded-lg">
                  <div className="text-customGreen text-lg font-semibold">Property History</div>
                  <div className="text-sm text-gray-600 mt-1">Track all changes and updates to this property</div>
                </div>
                
                {propertyHistory.length > 0 ? (
                  <div 
                    ref={propertyScrollRef}
                    className="h-[500px] overflow-x-auto flex items-start  pr-2 custom-scrollbar"
                  >
                    <Timeline
                      className="px-4"
                      value={propertyHistory}
                      opposite={(item) => (
                        <div className="text-right">
                          <div className="text-xs text-gray-500 font-medium">
                            {item.timestamp}
                          </div>
                          {item.changeBy && (
                            <div className="text-xs text-gray-400 mt-1">
                              by {item.changeBy.name}
                            </div>
                          )}
                        </div>
                      )}
                      content={(item) => (
                        <div className="ml-4">
                          <div className="text-sm line-clamp-2 text-wrap truncate font-medium text-gray-900 mb-1">
                            {item.log}
                          </div>
                            {/* {item.action && (
                              <div className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {formatActionName(item.action)}
                              </div>
                            )} */}
                        </div>
                      )}
                      marker={(item) => (
                        <span
                          className="custom-marker flex items-center justify-center w-3 h-3 rounded-full border-2"
                          style={{ 
                            borderColor: "#399E79",
                            backgroundColor: "white",
                            boxShadow: "0 0 0 4px rgba(57, 158, 121, 0.1)"
                          }}
                        />
                      )}
                    />
                    
                    {/* Loading indicator for property history */}
                    {isFetchingProperty.current && (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-customGreen"></div>
                      </div>
                    )}
                    
                    {/* End of data indicator */}
                    {/* {!hasMoreProperty && propertyHistory.length > 0 && (
                      <div className="text-center py-4 text-sm text-gray-500">
                        No more history records
                      </div>
                    )} */}
                  </div>
                ) : (
                  !loading && (
                    <EmptyHistoryState 
                      title="No Property History Found"
                      description="There are no recorded activities or changes for this property yet."
                    />
                  )
                )}
              </div>
            )}

            {activeTab === "Sitting History" && (
              <div>
                <div className="bg-slate-100 p-3 mb-4 rounded-lg">
                  <div className="text-customGreen text-lg font-semibold">Sitting History</div>
                  <div className="text-sm text-gray-600 mt-1">Track all sitting activities and meetings for this property</div>
                </div>
                
                {sittingHistory.length > 0 ? (
                  <div 
                    ref={sittingScrollRef}
                    className="h-[500px] overflow-y-auto pr-2 custom-scrollbar"
                  >
                    <Timeline
                      className="px-4"
                      value={sittingHistory}
                      opposite={(item) => (
                        <div className="text-right">
                          <div className="text-xs text-gray-500 font-medium">
                            {item.timestamp}
                          </div>
                          {item.changeByUser && (
                            <div className="text-xs text-gray-400 mt-1">
                              by {item.changeByUser.name} ({item.changeByUser.role})
                            </div>
                          )}
                        </div>
                      )}
                      content={(item) => (
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            {item.log}
                          </div>
                          <div className="flex gap-2">
                            {item.action && (
                              <div className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                {formatActionName(item.action)}
                              </div>
                            )}
                            {item.sittingId && (
                              <div className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                                ID: {item.sittingId}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      marker={(item) => (
                        <span
                          className="custom-marker flex items-center justify-center w-3 h-3 rounded-full border-2"
                          style={{ 
                            borderColor: "#399E79",
                            backgroundColor: "white",
                            boxShadow: "0 0 0 4px rgba(57, 158, 121, 0.1)"
                          }}
                        />
                      )}
                    />
                    
                    {/* Loading indicator for sitting history */}
                    {isFetchingSitting.current && (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-customGreen"></div>
                      </div>
                    )}
                    
                    {/* End of data indicator */}
                    {!hasMoreSitting && sittingHistory.length > 0 && (
                      <div className="text-center py-4 text-sm text-gray-500">
                        No more history records
                      </div>
                    )}
                  </div>
                ) : (
                  !loading && (
                    <EmptyHistoryState 
                      title="No Sitting History Found"
                      description="There are no recorded sitting activities or meetings for this property yet."
                    />
                  )
                )}
              </div>
            )}

            {/* Initial loading state */}
            {loading && (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customGreen"></div>
                <span className="ml-3 text-gray-600">Loading history...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoughPropertyHistory